import _ from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactTable from "react-table";

import { connect } from 'react-redux';

import withStyles from "@material-ui/core/styles/withStyles";
import extendedTablesStyle from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle";

import Assignment from "@material-ui/icons/Assignment";
import SvgIcon from "@material-ui/core/SvgIcon";
import IconButton from "@material-ui/core/IconButton";
import ClearIcon from '@material-ui/icons/Clear';
import SendIcon from '@material-ui/icons/Send';
import Button from '@material-ui/core/Button';
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import Box from '@material-ui/core/Box';
import CardBody from "components/Card/CardBody";
import CardIcon from "components/Card/CardIcon";
import CardHeader from "components/Card/CardHeader";
import Loading from "../../components/Loading/Loading";
import { loaderStyleEmptyCard } from "../../helpers/CustomStyles";
import Snackbar from '../../../components/Snackbar/Snackbar.js';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import CustomButton from '../../../components/CustomButtons/ButtonTS';
import SweetAlert from 'react-bootstrap-sweetalert';
import { sweetAlertStyle, closeSweetAlertStyle } from '../../helpers/CustomStyles';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import CancelIcon from '@material-ui/icons/Cancel';

import {
    confirmNotification,
    logsData,
    closeConfirmedLogNotification,
    getSystemsNodesShort,
    clearLogs,
    sendLog
} from '../../stores/actions/logsActions';
import AcknowledgeLogButton from "./AcknowledgeLogButton";
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { exportRowsToCsv, withTimestamp } from '../../helpers/exportCsv';

import './LogsReactTable.css';

const MAX_SYSTEMS = 3;
const MAX_DATE_RANGE_DAYS = 14;

function SeverityIcon(props) {
    return (
        <SvgIcon {...props}>
            <path d="M4.47 21h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18c-.77 1.33.19 3 1.73 3zM12 14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" />
        </SvgIcon>
    );
}

const getSeverityColor = (severity) => {
    switch (severity) {
        case "L1": return '#7df200';
        case "L2": return '#ffe11c';
        case "L3": return '#ff9900';
        case "L4": return '#ff0000';
        case "obstruction": return '#f058d4';
        default: return '#787878';
    }
};

const defaultDateRange = () => {
    const now = new Date();
    const from = new Date();
    from.setDate(from.getDate() - MAX_DATE_RANGE_DAYS);
    return [from, now];
};

const parseDateRange = (range) => {
    if (!range) return defaultDateRange();
    const [startStr, endStr] = range.split(',');
    return [new Date(startStr), new Date(endStr)];
};

function LogsReactTable(props) {
    const {
        classes,
        isLogsLoading,
        logs,
        mobileView,
        confirmNotification,
        confirmedLogSuccessfully,
        confirmedLogFailed,
        closeConfirmedLogNotification,
        systemsNodesShort,
        loggedUserId,
        location,
        history,
    } = props;

    const systems = useMemo(() => {
        const unique = Object.values(
            systemsNodesShort.reduce((acc, item) => {
                if (!acc[item.systemId]) {
                    acc[item.systemId] = { ID: item.systemId, name: item.systemName };
                }
                return acc;
            }, {})
        );
        return [...unique].sort((a, b) => a.name > b.name ? 1 : -1);
    }, [systemsNodesShort]);

    const systemNamesByIds = useMemo(() =>
        Object.fromEntries(systems.map(x => [x.ID, x.name])),
        [systems]
    );
    const nodeNamesByIds = useMemo(() =>
        Object.fromEntries(systemsNodesShort.map(x => [x.nodeId, x.nodeName])),
        [systemsNodesShort]
    );

    const queryParams = new URLSearchParams(location.search);
    const systemParam = queryParams.get("system");
    const nodeParam = queryParams.get("node");
    const textParam = queryParams.get("text");
    const dateRangeParam = queryParams.get("range");

    const initialSystemIds = systemParam
        ? systemParam.split(',').map(Number)
        : [];
    const initialNodeId = nodeParam ? parseInt(nodeParam) : null;
    const initialDate = dateRangeParam ? parseDateRange(dateRangeParam) : defaultDateRange();

    // ─── Filter state ─────────────────────────────────────────────────────────
    const [pickedSystemIds, setPickedSystemIds] = useState(initialSystemIds);
    const [pickedNodeId, setPickedNodeId] = useState(initialNodeId);
    const [searchedText, setSearchedText] = useState(textParam ?? '');
    const [date, setDate] = useState(initialDate);
    const [dateErrorOpen, setDateErrorOpen] = useState(false);

    // ─── Table state ──────────────────────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(mobileView ? 100 : 25);
    const [sortBy, setSortBy] = useState('eventTimestamp');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [messageFilter, setMessageFilter] = useState('');

    // ─── Mobile view ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (mobileView) {
            // ukryj header tabeli
            const tableHeader = document.querySelector(".rt-tr");
            if (tableHeader) tableHeader.style.display = "none";

            // dodaj opisy kolumn
            const rows = [...document.querySelectorAll(".ReactTable .rt-table .rt-tbody .rt-tr-group .rt-tr .rt-td")];
            const descriptions = ["Severity: ", "System name: ", "Device name: ", "ID: ", "Date: ", "Message: "];
            let counter = 0;
            rows.forEach((row, i) => {
                if (i % 2 === 0) {
                    row.style.display = "initial";
                    row.textContent = descriptions[counter];
                    row.style.fontWeight = "500";
                    counter++;
                    if (counter === descriptions.length) counter = 0;
                }
            });

            // podkreślenia wierszy
            const logs = [...document.querySelectorAll(".ReactTable .rt-table .rt-tbody .rt-tr-group .rt-tr")];
            logs.forEach(log => {
                log.style.borderBottom = "1px solid #c8c8c8";
                log.style.padding = "10px 0";
            });

            // ukryj środkową część paginacji
            [...document.querySelectorAll(".-center")].forEach(el => el.style.display = "none");

            // marginesy przycisków paginacji
            const previousButtonTop = document.querySelector(".pagination-top .-previous");
            const nextButtonTop = document.querySelector(".pagination-top .-next");
            const previousButtonBottom = document.querySelector(".pagination-bottom .-previous");
            const nextButtonBottom = document.querySelector(".pagination-bottom .-next");
            if (previousButtonTop) previousButtonTop.style.margin = "0 10px 15px 0";
            if (nextButtonTop) nextButtonTop.style.margin = "0 0 15px 10px";
            if (previousButtonBottom) previousButtonBottom.style.margin = "15px 10px 0 0";
            if (nextButtonBottom) nextButtonBottom.style.margin = "15px 0 0 10px";

        } else {
            // przywróć header
            const tableHeader = document.querySelector(".rt-tr");
            if (tableHeader) tableHeader.style.display = "flex";

            // usuń podkreślenia
            const logs = [...document.querySelectorAll(".ReactTable .rt-table .rt-tbody .rt-tr-group .rt-tr")];
            logs.forEach(log => {
                log.style.borderBottom = "0";
                log.style.padding = "0";
            });

            // przywróć środkową część paginacji
            [...document.querySelectorAll(".-center")].forEach(el => el.style.display = "initial");

            // usuń marginesy
            const previousButtonBottom = document.querySelector(".pagination-bottom .-previous");
            const nextButtonBottom = document.querySelector(".pagination-bottom .-next");
            if (previousButtonBottom) previousButtonBottom.style.margin = "0";
            if (nextButtonBottom) nextButtonBottom.style.margin = "0";
        }
    }, [mobileView, logs]); // logs żeby odpalało się po załadowaniu logów

    // ─── Notification timeout ─────────────────────────────────────────────────
    useEffect(() => {
        if (!confirmedLogSuccessfully && !confirmedLogFailed) return;
        const t = setTimeout(() => closeConfirmedLogNotification(), 5000);
        return () => clearTimeout(t);
    }, [confirmedLogSuccessfully, confirmedLogFailed]);

    // ─── Auto-select systems if <= MAX_SYSTEMS ────────────────────────────────
    useEffect(() => {
        if (
            systems.length > 0 &&
            systems.length <= MAX_SYSTEMS &&
            pickedSystemIds.length === 0
        ) {
            setPickedSystemIds(systems.map(s => s.ID));
        }
    }, [systems]);

    // ─── On mount: fetch systems/nodes ────────────────────────────────────────
    useEffect(() => {
        closeConfirmedLogNotification();
        props.getSystemsNodesShort();
        props.clearLogs();
    }, []);

    // ─── Helpers ──────────────────────────────────────────────────────────────
    const updateSearchParams = useCallback((updates) => {
        const searchParams = new URLSearchParams(location.search);
        Object.entries(updates).forEach(([key, value]) => {
            if (value == null || value === '') {
                searchParams.delete(key);
            } else {
                searchParams.set(key, value);
            }
        });
        history.push(`${location.pathname}?${searchParams.toString()}`);
    }, [location, history]);

    const validateDateRange = (newDate) => {
        if (!newDate || !newDate[0] || !newDate[1]) return true;
        const diffMs = newDate[1].getTime() - newDate[0].getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays <= MAX_DATE_RANGE_DAYS;
    };

    const buildDateIso = (d) => {
        if (!d || !d[0] || !d[1]) return { dateStart: null, dateEnd: null };
        return {
            dateStart: d[0].toISOString(),
            dateEnd: d[1].toISOString(),
        };
    };

    // ─── Get logs ─────────────────────────────────────────────────────────────
    const fetchLogs = useCallback((overrides = {}) => {
        const systemIds = overrides.pickedSystemIds ?? pickedSystemIds;
        if (systemIds.length === 0) return;
        const nodeId = overrides.pickedNodeId ?? pickedNodeId;
        const text = overrides.searchedText ?? searchedText;
        const d = overrides.date ?? date;
        const { dateStart, dateEnd } = buildDateIso(d);

        props.clearLogs();
        props.logsData(
            systemIds,
            nodeId,
            text || null,
            dateStart,
            dateEnd
        );

        // update query params
        updateSearchParams({
            system: systemIds.length ? systemIds.join(',') : null,
            node: nodeId ?? null,
            text: text || null,
            range: d && d[0] && d[1]
                ? `${d[0].toISOString().split('T')[0]},${d[1].toISOString().split('T')[0]}`
                : null,
        });

        setPage(0);
    }, [pickedSystemIds, pickedNodeId, searchedText, date, sortBy, sortOrder]);

    // ─── Move back / forward ───────────────────────────────────────────────────
    const handleMoveBack = () => {
        if (!date || !date[0] || !date[1]) return;
        const diffMs = date[1].getTime() - date[0].getTime();
        const newTo = new Date(date[0].getTime());
        const newFrom = new Date(date[0].getTime() - diffMs);
        const newDate = [newFrom, newTo];

        setDate(newDate);
        fetchLogs({ date: newDate });
    };

    const handleMoveForward = () => {
        if (!date || !date[0] || !date[1]) return;
        const diffMs = date[1].getTime() - date[0].getTime();
        const newFrom = new Date(date[1].getTime());
        const newTo = new Date(date[1].getTime() + diffMs);

        // nie przekraczaj dzisiaj
        const now = new Date();
        if (newFrom > now) return;

        if (!validateDateRange([newFrom, newTo])) {
            setDateErrorOpen(true);
            return;
        }

        setDate([newFrom, newTo]);
        fetchLogs({ date: [newFrom, newTo] });
    };

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handlePickSystems = (event, newValues) => {
        if (newValues.length > MAX_SYSTEMS) return; // silently block >3
        setPickedSystemIds(newValues);
        setPickedNodeId(null);
    };

    const handlePickNode = (event, newValue) => {
        setPickedNodeId(newValue);
    };

    const handleDateChange = (newDate) => {
        if (!newDate) { setDate(null); return; }
        const [from, to] = newDate;
        
        if (from && to && from.getTime() === to.getTime()) return;
        if (from && to && !validateDateRange(newDate)) {
            setDateErrorOpen(true);
            return;
        }
        setDate(newDate);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    // ─── Filtered + sorted logs (frontend only) ───────────────────────────────
    const filteredLogs = useMemo(() => {
        let result = logs ?? [];
        if (messageFilter) {
            result = result.filter(log =>
                log.messageEn?.toLowerCase().includes(messageFilter.toLowerCase())
            );
        }
        return result;
    }, [logs, messageFilter]);

    // ─── Nodes for picked systems ─────────────────────────────────────────────
    const systemNodes = useMemo(() =>
        systemsNodesShort.filter(n => pickedSystemIds.includes(n.systemId)),
        [systemsNodesShort, pickedSystemIds]
    );
    const nodeIds = systemNodes.map(n => n.nodeId);

    // ─── Columns ──────────────────────────────────────────────────────────────
    const mobileColumns = [
        { Header: "" },
        { Header: "Severity", accessor: "severity" },
        { Header: "" },
        { Header: "System", accessor: "systemName" },
        { Header: "" },
        { Header: "Device", accessor: "nodeName", sortable: false },
        { Header: "" },
        { Header: "#", accessor: "id", sortable: false },
        { Header: "" },
        {
            Header: "Date", accessor: "eventTimestamp", sortable: false,
            Cell: props => props.value ? props.value.toLocaleString('sv-SE').replace('T', ' ') : null
        },
        { Header: "" },
        { Header: "Message", accessor: "message", sortable: false }
    ];

    const desktopColumns = useMemo(() => {
        const cols = [
            { Header: "Severity", accessor: "severity", width: 90 },
            { Header: "System", accessor: "systemName", minWidth: 120, flex: 1 },
            { Header: "Device", accessor: "nodeName", sortable: false, width: 110 },
            { Header: "#", accessor: "id", width: 80 },
            {
                Header: "Date", accessor: "eventTimestamp", minWidth: 90,
                Cell: props => props.value ? props.value.toLocaleString('sv-SE').replace('T', ' ') : null
            },
            { Header: "Message", accessor: "message", sortable: false, minWidth: 190, flex: 2},
            { Header: "Confirm", accessor: "confirm", sortable: false, width: 120 }
        ];
        if (loggedUserId == 135) {
            cols.push({ Header: "Send", accessor: "send", sortable: false, width: 60 });
        }
        return cols;
    }, [messageFilter, loggedUserId]);

    const exportLogs = useCallback(() => {
        exportRowsToCsv(withTimestamp('logs'), [
            { header: 'Severity', field: 'severity' },
            { header: 'System', field: 'systemName' },
            { header: 'Device', value: (log) => nodeNamesByIds[log.relatedNodeId] },
            { header: 'ID', field: 'id' },
            { header: 'Date', value: (log) => log.eventTimestamp ? new Date(log.eventTimestamp).toLocaleString('sv-SE').replace('T', ' ') : '' },
            { header: 'Message', field: 'messageEn' },
        ], filteredLogs);
    }, [filteredLogs, nodeNamesByIds]);

    // ─── Table data ───────────────────────────────────────────────────────────
    const tableData = useMemo(() =>
        filteredLogs.map((log) => {
            const color = getSeverityColor(log.severity);
            const severity = (
                <SeverityIcon
                    className={classes.icon}
                    color="primary"
                    fontSize="large"
                    component={svgProps => (
                        <svg {...svgProps}>
                            {React.cloneElement(svgProps.children[0], { fill: color })}
                        </svg>
                    )}
                />
            );
            return {
                send: (
                    <>
                        <CustomButton simple justIcon color='info' onClick={() => props.sendLog(log.id, false)} title="Send">
                            <SendIcon style={{ width: '20px', height: '20px' }} />
                        </CustomButton>
                        <CustomButton simple justIcon color='reddit' onClick={() => props.sendLog(log.id, true)} title="Update">
                            <SendIcon style={{ width: '20px', height: '20px' }} />
                        </CustomButton>
                    </>
                ),
                severity,
                systemName: log.systemName,
                nodeName: nodeNamesByIds[log.relatedNodeId],
                id: log.id,
                message: log.messageEn,
                eventTimestamp: log.eventTimestamp ? new Date(log.eventTimestamp) : null,
                confirm: (
                    <AcknowledgeLogButton
                        severity={log.severity}
                        systemVersion={log.systemVersion}
                        confirmed={log.confirmed}
                        confirmable={log.confirmable}
                        ID={log.id}
                        msgTemplateId={log.msgTemplateId}
                        confirmNotification={confirmNotification}
                        confirmedLogFailed={confirmedLogFailed}
                        confirmedLogSuccessfully={confirmedLogSuccessfully}
                    />
                )
            };
        }),
        [filteredLogs, nodeNamesByIds]
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div data-testid="LogsReactTable">
            <GridContainer className="logs-page">
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary"><Assignment /></CardIcon>
                            <h4 className={classes.cardIconTitle}>System logs</h4>
                        </CardHeader>
                        <CardBody>
                            {/* ── Filters ── */}
                            <Box style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                flexDirection: mobileView ? 'column' : 'row',
                                alignItems: 'flex-end',
                                flexWrap: 'wrap',
                                gap: 12,
                                width: '100%'
                            }}>
                                {/* Systems multi-select */}
                                <Autocomplete
                                    multiple
                                    options={systems.map(s => s.ID)}
                                    getOptionLabel={(id) => systemNamesByIds[id] ?? ''}
                                    value={pickedSystemIds}
                                    onChange={handlePickSystems}
                                    getOptionDisabled={() => pickedSystemIds.length >= MAX_SYSTEMS}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={`Filter by system (max ${MAX_SYSTEMS})`}
                                            margin='normal'
                                        />
                                    )}
                                    style={{ display: 'flex', width: mobileView ? '100%' : '30%' }}
                                />

                                {/* Node */}
                                <Autocomplete
                                    options={nodeIds}
                                    getOptionLabel={(id) => nodeNamesByIds[id] ?? ''}
                                    value={pickedNodeId}
                                    onChange={handlePickNode}
                                    renderInput={(params) => (
                                        <TextField {...params} label='Filter by device' margin='normal' />
                                    )}
                                    style={{ display: 'flex', width: mobileView ? '100%' : '20%' }}
                                    disabled={pickedSystemIds.length === 0}
                                />

                                {/* Text search */}
                                <TextField
                                    label="Search in messages"
                                    margin='normal'
                                    value={searchedText}
                                    style={{ display: 'flex', width: mobileView ? '100%' : '20%' }}
                                    disabled={pickedSystemIds.length === 0}
                                    onChange={(e) => setSearchedText(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') fetchLogs({ searchedText: e.target.value });
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                size="small"
                                                disabled={!searchedText}
                                                onClick={() => { setSearchedText(''); }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }}
                                />

                                {/* Date range */}
                                <Box style={{ display: 'flex', flexDirection: 'column', width: mobileView ? '100%' : '20%' }}>
                                    <DateRangePicker
                                        locale="en-US"
                                        onChange={handleDateChange}
                                        value={date}
                                        format="y-MM-dd"
                                        clearIcon={<ClearIcon fontSize="small" style={{ visibility: date ? 'visible' : 'hidden' }} />}
                                        calendarIcon={null}
                                        autoFocus={false}
                                        maxDate={new Date()}
                                        dayPlaceholder={'DD'}
                                        monthPlaceholder={'MM'}
                                        yearPlaceholder={'YYYY'}
                                        rangeDivider={'-'}
                                    />
                                </Box>
                            </Box>

                            {/* ── Action buttons ── */}
                            <Box style={{ display: 'flex', gap: 12, marginTop: 8, marginBottom: 16, justifyContent: 'center', paddingTop: '10px' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disabled={pickedSystemIds.length === 0 || !date || !date[0] || !date[1]}
                                    onClick={handleMoveBack}
                                >
                                    Move back and get logs
                                </Button>
                               <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={pickedSystemIds.length === 0 || !date || !date[0] || !date[1]}
                                    onClick={() => fetchLogs()}
                                >
                                    Get logs
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disabled={
                                        pickedSystemIds.length === 0 || 
                                        !date || !date[0] || !date[1] ||
                                        date[1] >= new Date(new Date().setHours(23, 59, 59, 999))
                                    }
                                    onClick={handleMoveForward}
                                >
                                    Move forward and get logs
                                </Button>
                                {!mobileView && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={!filteredLogs.length}
                                        onClick={exportLogs}
                                    >
                                        Export
                                    </Button>
                                )}
                            </Box>

                            {/* ── Table ── */}
                            {isLogsLoading ? (
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    minHeight: 200 
                                }}>
                                    <Loading transform={0.85} height={90} width={70} color="gray" />
                                </div>
                            ) : (
                            <ReactTable
                                data={tableData}
                                filterable={false}
                                columns={mobileView ? mobileColumns : desktopColumns}
                                page={page}
                                pageSize={pageSize}
                                minRows={3}
                                showPaginationTop={mobileView}
                                showPaginationBottom={true}
                                sorted={[{ id: sortBy, desc: sortOrder === 'DESC' }]}
                                className={mobileView ? "-striped -highlight logsMobileView" : "-striped -highlight"}
                                onPageChange={p => setPage(p)}
                                onPageSizeChange={handlePageSizeChange}
                                onSortedChange={(newSorted) => {
                                    if (!newSorted.length) return;
                                    const newSortBy = newSorted[0].id;
                                    const sortByChanged = newSortBy !== sortBy;
                                    const newSortOrder = sortByChanged ? 'ASC' : (sortOrder === 'ASC' ? 'DESC' : 'ASC');
                                    setSortBy(newSortBy);
                                    setSortOrder(newSortOrder);
                                    setPage(0);
                                }}
                            />
                            )}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            {/* ── Date range error dialog ── */}
            {dateErrorOpen && (
                <SweetAlert
                    style={{ sweetAlertStyle }}
                    title=""
                    onConfirm={() => setDateErrorOpen(false)}
                    showConfirm={false}
                >
                    <ErrorComponent Children message='Something went wrong'>
                        <Card style={{ overflow: 'visible', marginTop: '10px', marginBottom: '0', boxShadow: 'none' }}>
                            <CardHeader color='info' icon>
                                <CardIcon color='info'>
                                    <Assignment />
                                </CardIcon>
                                <h3 style={{ color: '#3C4858', marginTop: '15px', marginBottom: '0' }}>
                                    Date range too wide
                                </h3>
                            </CardHeader>
                            <CardBody>
                                <GridContainer>
                                    <GridItem xs={12}>
                                        <h5>Maximum allowed date range is {MAX_DATE_RANGE_DAYS} days. Please select a narrower range.</h5>
                                    </GridItem>
                                    <GridItem xs={12} style={{ margin: '15px 0 0 0' }}>
                                        <CustomButton color='info' onClick={() => setDateErrorOpen(false)} className='downloadButton'>
                                            OK
                                        </CustomButton>
                                    </GridItem>
                                    {/* <GridItem xs={12} style={{ margin: '25px 0 0 0' }}>
                                        <Button
                                            color='info'
                                            onClick={() => this.handleClick()}
                                            disabled={disableButton}
                                            data-testid='thresholdButton'
                                            className='downloadButton'
                                        >
                                            Save
                                        </Button>
                                    </GridItem> */}
                                </GridContainer>
                            </CardBody>
                        </Card>
                    </ErrorComponent>
                </SweetAlert>
            )}

            {/* ── Notification ── */}
            <Snackbar
                close
                closeNotification={() => props.closeConfirmedLogNotification()}
                color={!confirmedLogFailed && !confirmedLogSuccessfully ? 'info' : confirmedLogSuccessfully ? 'success' : 'danger'}
                message={
                    confirmedLogSuccessfully
                        ? 'Log was confirmed successfully.'
                        : confirmedLogFailed
                            ? 'Log was not confirmed.'
                            : ''
                }
                open={confirmedLogFailed || confirmedLogSuccessfully}
                place='tc'
            />
        </div>
    );
}

const mapStateToProps = (state) => ({
    isLogsLoading: state.dashboard.isLogsLoading,
    logs: state.dashboard.logs,
    mobileView: state.dashboard.mobileView,
    confirmedLogSuccessfully: state.dashboard.confirmedLogSuccessfully,
    confirmedLogFailed: state.dashboard.confirmedLogFailed,
    systemsNodesShort: state.dashboard.systemsNodesShort,
    loggedUserId: state.login.loggedUserId,
});

const actions = {
    confirmNotification,
    logsData,
    closeConfirmedLogNotification,
    getSystemsNodesShort,
    clearLogs,
    sendLog,
};

export default connect(mapStateToProps, actions)(withStyles(extendedTablesStyle)(LogsReactTable));
